import { useCallback, useMemo, useRef } from 'react'

import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { Table, VStack } from 'govwell-ui'
import TableActionToolbar from 'govwell-ui/components/Table/TableActionToolbar'
import { RowLevelAction } from 'govwell-ui/types'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react-lite'
import { CoordinateFragment, ViewDataRowFragment } from 'types/graphql'

import { LoadingSpin } from 'src/components/shared/StyledComponents'
import TableViewCell from 'src/components/TableViews/TableViewCell'
import { TableViewColumnOverrides } from 'src/components/TableViews/TableViewColumnOverrides'
import { TableViewMode } from 'src/fetch/views'

import { UseTableViewManagerReturn } from '../fetch/views/use-table-view-manager'
import { DataMap, MapShapeAction } from '../pages/DataMap/DataMap'
import { RefetchFnType } from '../types'
import { filterNil } from '../utils'

import { getShowActionToolbar } from './getShowActionToolbar'
import TableViewControls from './TableViews/TableViewControls'

type Props<TData extends { id: number }> = {
  compactBannerContent?: React.ReactNode
  emptyStateMessage: string
  getAppendedColumns?: (refetch: RefetchFnType) => ColumnDef<ViewDataRowFragment>[]
  getDataFromRow: (row: ViewDataRowFragment) => TData | null | undefined
  getPrependedColumns?: (refetch: RefetchFnType) => ColumnDef<ViewDataRowFragment>[]
  getRowIsSelectable?: (rowData: ViewDataRowFragment) => boolean
  getRowSelectionTooltip?: (rowData: ViewDataRowFragment) => string | undefined
  tableHeader?: React.ReactNode
  rowLevelActions?: {
    primaryActions?: RowLevelAction[]
    secondaryActions?: RowLevelAction[]
  }
  isCsvEnabled?: boolean
  /** When set, renders the action toolbar outside Table (view-wide bulk selection count). */
  externalActionToolbarSelectedRowCount?: number
  onClearSelections?: () => void
  map?: {
    getDescription: (data: TData[]) => string | null | undefined
    getCoordinates: (datum: TData) => CoordinateFragment[] | null | undefined
    getId?: (datum: TData) => string | number
    mapShapeActions: MapShapeAction<TData>[]
    getTitle: (datum: TData) => string
  }
  mode?: TableViewMode
  tableViewState: UseTableViewManagerReturn
}

function TableViewBuilder<TData extends { id: number }>({
  compactBannerContent,
  getPrependedColumns,
  getAppendedColumns,
  getDataFromRow,
  getRowIsSelectable,
  getRowSelectionTooltip,
  isCsvEnabled = true,
  emptyStateMessage,
  map,
  mode = TableViewMode.Edit,
  tableHeader,
  rowLevelActions,
  externalActionToolbarSelectedRowCount,
  onClearSelections,
  tableViewState,
}: Props<TData>) {
  const {
    columnTemplates,
    generateDefaultViews,
    getRowId,
    hasViewsDataLoaded,
    isFetchingViewData,
    isLoadingViewData,
    isLoadingViews,
    onPaginationChange,
    onRowSelectionChange,
    onSaveViewChanges,
    onSortingChange,
    pagination,
    refetchDatabaseCountForView,
    refetchViewData,
    rows,
    rowSelection,
    sorting,
    searchQuery,
    selectedRows,
    setSearchQuery,
    tableViewManager,
    viewCountLoadingStates,
    viewType,
  } = tableViewState

  const searchInputRef = useRef<HTMLInputElement>(null)

  const handleRowActionCompleted = useCallback(async () => {
    await runInAction(async () => {
      const selectedView = tableViewManager.selectedView
      if (!selectedView) {
        return
      }

      onRowSelectionChange({})
      return await Promise.all([refetchDatabaseCountForView(selectedView), refetchViewData()])
    })
  }, [
    onRowSelectionChange,
    refetchDatabaseCountForView,
    refetchViewData,
    tableViewManager.selectedView,
  ])

  const selectedViewColumns = tableViewManager.selectedView?.columnManager.columns
  const columns = useMemo(() => {
    if (!columnTemplates?.length) {
      return []
    }
    const columnHelper = createColumnHelper<ViewDataRowFragment>()
    const baseColumns = filterNil(
      selectedViewColumns?.map((c, index) => {
        if (!c.columnTemplate) {
          return null
        }

        const override = TableViewColumnOverrides[c.columnTemplate.columnType] ?? null
        const defaultHeader = c.columnTemplate.labelTitleCase

        return columnHelper.accessor((row) => row.columns[index], {
          id: c.columnTemplate.generatedId,
          header: override?.renderHeader
            ? () =>
                c.columnTemplate
                  ? override.renderHeader?.({
                      columnTemplate: c.columnTemplate,
                      defaultHeader,
                    })
                  : defaultHeader
            : defaultHeader,
          cell: (cell) => (
            <TableViewCell
              columnTemplate={c.columnTemplate}
              row={cell.row.original}
              data={cell.getValue()}
              viewType={viewType}
            />
          ),
          enableSorting: c.columnTemplate.isSortable,
        })
      }) ?? []
    )
    const prependedColumns = getPrependedColumns?.(handleRowActionCompleted) || []
    const appendedColumns = getAppendedColumns?.(handleRowActionCompleted) || []
    return [...prependedColumns, ...baseColumns, ...appendedColumns]
  }, [
    columnTemplates?.length,
    getPrependedColumns,
    getAppendedColumns,
    handleRowActionCompleted,
    selectedViewColumns,
    viewType,
  ])
  const data = useMemo(() => filterNil(rows.map(getDataFromRow) ?? []), [getDataFromRow, rows])

  const useExternalActionToolbar = externalActionToolbarSelectedRowCount != null

  const handleClearSelections = useCallback(() => {
    if (onClearSelections) {
      onClearSelections()
      return
    }
    onRowSelectionChange({})
  }, [onClearSelections, onRowSelectionChange])

  const showActionToolbar = getShowActionToolbar({
    externalActionToolbarSelectedRowCount,
    hasRowLevelActions: !!rowLevelActions,
    pageSelectedRowCount: selectedRows.length,
  })
  const tableRowLevelActions = useMemo(() => {
    if (!showActionToolbar || useExternalActionToolbar) {
      return undefined
    }
    return rowLevelActions
  }, [rowLevelActions, showActionToolbar, useExternalActionToolbar])

  const isInitialLoading = isLoadingViews || isLoadingViewData || !hasViewsDataLoaded
  return (
    <VStack align="stretch" gap={4}>
      <TableViewControls
        compactBannerContent={compactBannerContent}
        generateDefaultViews={generateDefaultViews}
        isLoadingViewData={isLoadingViewData}
        isLoadingViews={isLoadingViews}
        mode={mode}
        onSaveViewChanges={onSaveViewChanges}
        onSelectedViewChange={refetchDatabaseCountForView}
        refetchViewData={refetchViewData}
        searchInputRef={searchInputRef}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        tableViewManager={tableViewManager}
        isCsvEnabled={isCsvEnabled}
        viewCountLoadingStates={viewCountLoadingStates}
      />
      {map && (
        <DataMap<TData>
          data={data}
          isLoading={isLoadingViews || isLoadingViewData}
          getDescription={map.getDescription}
          getCoordinates={map.getCoordinates}
          getId={map.getId}
          mapShapeActions={map.mapShapeActions}
          getTitle={map.getTitle}
        />
      )}
      {tableHeader}
      <Table
        size="lg"
        columns={columns}
        data={rows}
        emptyStateImage={isInitialLoading ? null : undefined}
        emptyStateMessage={isInitialLoading ? <LoadingSpin /> : emptyStateMessage}
        enablePagination
        enableRowSelection={!!rowLevelActions}
        enableSearchShortcut
        externalSearchInputRef={searchInputRef}
        getRowId={getRowId}
        getRowIsSelectable={getRowIsSelectable}
        getRowSelectionTooltip={getRowSelectionTooltip}
        isLoading={isLoadingViews || isFetchingViewData}
        onPaginationChange={onPaginationChange}
        onRowSelectionChange={onRowSelectionChange}
        onSortingChange={onSortingChange}
        pagination={pagination}
        rowLevelActions={tableRowLevelActions}
        rowSelection={rowSelection}
        sorting={sorting}
        totalCount={tableViewManager.selectedView?.totalCount}
      />
      {useExternalActionToolbar && showActionToolbar && rowLevelActions && (
        <TableActionToolbar
          onClearSelections={handleClearSelections}
          rowLevelActions={rowLevelActions}
          selectedRowCount={externalActionToolbarSelectedRowCount}
        />
      )}
    </VStack>
  )
}
export default observer(TableViewBuilder)
